/**
 * Decode an audio file and compute a dense min/max peaks array for
 * high-definition waveform rendering.
 * Also — v19.20 — extracts audio from video containers (MP4, MOV, WebM,
 * MKV, AVI) whose audio codec the browser supports natively. Returns
 * an optional `audioUrl` field pointing to a pure-WAV blob URL when
 * the input was a video, so Tone.Player never has to decode a video
 * container itself.
 *
 * v19.27 — Peaks are now a `Float32Array` of INTERLEAVED [min, max]
 * pairs. Density scales with buffer length so a typical clip has ~4 k
 * pairs (many samples per pixel column at every zoom the user is
 * likely to hit). This unlocks the continuous per-pixel waveform
 * renderer in `Waveform` (Arranger.jsx) — no more blocky bars.
 * Consumers detect the new format via `peaksFormat === "pairs"`.
 */
export async function decodeAudioFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const buffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  const peaks = buildPeakPairs(buffer);
  // v19.20 — For video inputs (or anything without a native audio/*
  // mime), transcode the decoded AudioBuffer into a WAV blob so
  // downstream Tone.Player gets a clean audio-only URL. We only run
  // the encode when needed — a plain WAV drop still returns
  // `audioUrl: null` and the caller keeps its own object URL.
  const isVideo = (file.type || "").startsWith("video/") || /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(file.name);
  let audioUrl = null;
  if (isVideo) {
    const wavBlob = audioBufferToWavBlob(buffer);
    audioUrl = URL.createObjectURL(wavBlob);
  }
  try { ctx.close(); } catch {}
  return { peaks, peaksFormat: "pairs", duration: buffer.duration, audioUrl };
}

/**
 * Downsample an AudioBuffer into a dense Float32Array of interleaved
 * [min, max] pairs — one pair per bucket. Bucket count is chosen so
 * that even long recordings stay under ~64 KB of peak data while
 * short clips still get sub-pixel resolution.
 *
 * Layout: `[min0, max0, min1, max1, min2, max2, ...]`
 * `min` is always ≤ 0, `max` is always ≥ 0. Multi-channel inputs are
 * mixed down to mono (max of |ch0|, |ch1|) so the preview is
 * consistent regardless of stereo layout.
 */
export function buildPeakPairs(buffer) {
  const chLeft = buffer.getChannelData(0);
  const chRight = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null;
  // Target pair density: ~1 pair per 128 audio samples (≈344 pairs/sec
  // at 44.1 kHz) → smooth per-pixel drawing at any reasonable zoom.
  // Floor 500, cap 8000 — plenty of detail without runaway memory
  // (8000 pairs = 64 KB per clip).
  const desired = Math.floor(chLeft.length / 128);
  const pairs = Math.max(500, Math.min(8000, desired));
  const peaks = new Float32Array(pairs * 2);
  const blockSize = Math.max(1, Math.floor(chLeft.length / pairs));
  for (let i = 0; i < pairs; i++) {
    const start = i * blockSize;
    const end = Math.min(chLeft.length, start + blockSize);
    let minV = 0, maxV = 0;
    for (let j = start; j < end; j++) {
      const l = chLeft[j];
      if (l < minV) minV = l;
      else if (l > maxV) maxV = l;
      if (chRight) {
        const r = chRight[j];
        if (r < minV) minV = r;
        else if (r > maxV) maxV = r;
      }
    }
    peaks[i * 2] = minV;
    peaks[i * 2 + 1] = maxV;
  }
  return peaks;
}

/** Encode an AudioBuffer as a 16-bit PCM WAV Blob (interleaved). */
function audioBufferToWavBlob(buffer) {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length;
  const bytesPerSample = 2;
  const dataSize = length * numChannels * bytesPerSample;
  const ab = new ArrayBuffer(44 + dataSize);
  const view = new DataView(ab);
  const writeString = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);           // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);
  // Interleaved 16-bit samples.
  const channels = [];
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c));
  let offset = 44;
  for (let i = 0; i < length; i++) {
    for (let c = 0; c < numChannels; c++) {
      const s = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      offset += 2;
    }
  }
  return new Blob([ab], { type: "audio/wav" });
}
